import { selectElement } from "./domHelper";
// 내용이 많아서 domHelper에서 독립함

/**
 * 주어진 요소의 가시성(visibility) 변화를 감시하고 콜백 함수를 실행합니다.
 * 가시성은 요소의 display, visibility, opacity 속성 및 부모 요소들의 상태에 따라 결정됩니다.
 * DOM 구조 변화도 감지하여 관찰 대상 경로를 동적으로 업데이트합니다.
 *
 * @param {HTMLElement|string} selector - 감시할 DOM 요소 또는 요소를 선택하는 CSS 선택자
 * @param {Object|Function} callbackOrObject - 가시성 변화 시 실행할 핸들러 함수를 포함한 객체 또는 함수
 *   콜백 함수는 반환 객체를 인자로 받고 핸들러 객체를 반환해야 합니다.
 *   핸들러 객체는 다음 메서드를 포함할 수 있습니다:
 *     - visible(): 요소가 보이게 될 때 실행할 함수
 *     - invisible(): 요소가 보이지 않게 될 때 실행할 함수
 * 
 * @returns {Object} 다음 속성과 메서드를 포함하는 객체:
 *   - visibility (getter): 현재 요소의 가시성 상태 (boolean)
 *   - destroy(): 모든 관찰을 중지하고 리소스를 해제하는 메서드
 * 
 * @throws {Error} 요소를 찾을 수 없거나 유효하지 않은 callbackOrObject 파라미터일 경우
 * 
 * @example
 * // 선택자로 사용
 * const visibility = useVisibility('#myElement', {
 *   visible() {
 *     console.log('요소가 보입니다');
 *   },
 *   invisible() {
 *     console.log('요소가 보이지 않습니다');
 *   }
 * });
 * 
 * // DOM 요소로 사용
 * const element = document.querySelector('.target');
 * const visibility = useVisibility(element, (current) => {
 *   console.log('현재 가시성:', current.visibility);
 *   return {
 *     visible() {
 *       fetchData();
 *     },
 *     invisible() {
 *       cleanup();
 *     }
 *   };
 * });
 * 
 * // 감시 중지
 * visibility.destroy();
 */
export function useVisibility(selector, callbackOrObject) {
  const element = selectElement(selector);
  if (!element) {
    throw new Error(`useVisibility: 요소를 찾을 수 없습니다 => ${selector}`);
  }

  let handlers = null;
  let currentVisibility = isVisible(element);
  
  // 반환 객체 생성
  const returnObj = {
    get element() {
      return element;
    },
    get visible() {
      return currentVisibility;
    },
    destroy() {
      observer.disconnect();
      domObserver.disconnect();
      handlers = null;
    }
  };
  
  // 콜백 또는 객체로부터 핸들러 설정
  if (typeof callbackOrObject === "function") {
    handlers = callbackOrObject(returnObj);
  } else if (typeof callbackOrObject === "object") {
    handlers = callbackOrObject;
  } else {
    throw new Error("useVisibility: 두 번째 매개변수는 함수 또는 객체여야 합니다");
  }
  
  // 현재 관찰 대상 경로 계산 (element부터 body까지)
  function getObservedElements() {
    const elements = [];
    let currentEl = element;
    
    // element가 DOM에 있는지 확인
    if (!document.body.contains(element)) {
      return elements;
    }
    
    while (currentEl && currentEl !== document.body) {
      elements.push(currentEl);
      currentEl = currentEl.parentElement;
    }
    if (document.body) elements.push(document.body);
    
    return elements;
  }
  
  // 특정 요소가 관찰 대상 경로에 있는지 확인
  function isInObservedPath(el) {
    const observedElements = getObservedElements();
    return observedElements.includes(el);
  }

  // 요소가 보이는지 확인하는 함수
  function isVisible(el) {
    if (!el || !document.body.contains(el)) return false;
    
    const style = window.getComputedStyle(el);
    
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden') return false;
    if (style.opacity === '0') return false;
    
    // 부모 요소도 확인
    let parent = el.parentElement;
    while (parent) {
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.display === 'none') return false;
      if (parentStyle.visibility === 'hidden') return false;
      if (parentStyle.opacity === '0') return false;
      parent = parent.parentElement;
    }
    
    return true;
  }

  // 가시성 변경 확인 및 처리
  function checkVisibilityChange() {
    // 요소가 DOM에 없으면 보이지 않는 것으로 처리
    if (!document.body.contains(element)) {
      if (currentVisibility === true) {
        currentVisibility = false;
        if (typeof handlers.invisible === 'function') {
          handlers.invisible();
        }
      }
      return;
    }
    
    const newVisibility = isVisible(element);
    
    if (newVisibility !== currentVisibility) {
      currentVisibility = newVisibility;
      
      if (currentVisibility && typeof handlers.visible === 'function') {
        handlers.visible();
      } else if (!currentVisibility && typeof handlers.invisible === 'function') {
        handlers.invisible();
      }
    }
  }

  // MutationObserver 설정
  const observer = new MutationObserver((mutations) => {
    let shouldCheck = false;
    
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        // 변화가 발생한 요소가 현재 관찰 대상 경로에 있는지 확인
        if (isInObservedPath(mutation.target)) {
          shouldCheck = true;
          break;
        }
      }
    }
    
    if (shouldCheck) {
      checkVisibilityChange();
    }
  });

  // 요소와 모든 부모 요소 감시 설정
  function observeElementAndParents() {
    // 현재 관찰 대상 경로의 모든 요소 감시
    const observedElements = getObservedElements();
    
    for (const el of observedElements) {
      observer.observe(el, {
        attributes: true  // 모든 어트리뷰트 변화를 감시
      });
    }
  }

  // 감시 시작
  observeElementAndParents();

  // DOM 변화로 인해 관찰 대상 경로가 변경될 수 있으므로
  // 주기적으로 관찰 대상을 재설정하는 MutationObserver
  const domObserver = new MutationObserver(() => {
    // 요소가 DOM에서 제거되었거나 경로가 변경된 경우에 대비해
    // 모든 관찰 대상을 재설정
    observer.disconnect();
    observeElementAndParents();
    
    // 가시성도 확인
    checkVisibilityChange();
  });
  
  // 문서 전체의 구조 변화 감시
  domObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  // 초기 가시성 상태에 따라 핸들러 실행 하지 않음
  // if (currentVisibility && typeof handlers.visible === 'function') {
  //   handlers.visible();
  // } else if (!currentVisibility && typeof handlers.invisible === 'function') {
  //   handlers.invisible();
  // }

  return returnObj;
}
